// src/lib/shot-logic.ts
// The SINGLE home of the keeper rule. Extracted verbatim from
// PlayScene.runShot:284-301. IO-free: imports NOTHING from gameBus /
// gameSignals / storage and NEVER branches on mode (only reads moodDelta), so
// the canonical daily can never get different odds. See spec §3.1.
//
// HAZARD: import the keeper fns as LIVE named bindings — do NOT alias them at
// module scope (e.g. `const fn = sampleKeeperZone`). tests/unit/
// play-scene-callout-events.test.ts does `vi.mock('@/lib/keeper')`; aliasing at
// init would capture the pre-mock value and break interception. (spec §3.1, codex)
import { sampleKeeperZone, applyMoodShift } from '@/lib/keeper';
import { aimToZone, type SwipeVector, type SwipeOutcome } from '@/lib/swipe-math';
import { resolveShot, type Outcome } from '@/lib/shot-resolution';
import { ZONES, type Zone } from '@/lib/zones';
import type { ZoneDistribution } from '@/lib/archetypes';
import type { ShotResult } from '@/lib/local-state';

export interface ShotLogicResult {
  swipe: SwipeOutcome;
  keeperZone: Zone;
  outcome: Outcome;
  nextDist: ZoneDistribution;
  shotResult: ShotResult;
}

/**
 * Pure shot resolution: aim + keeper sample + outcome + post-shot mood-shift.
 * Returns `swipe` (the caller passes it to the dive/ball animation) and
 * `nextDist` (the keeper distribution after this shot). Off-frame shots leave
 * the distribution unchanged. PRNG draw order is load-bearing: aim first
 * (`aimToZone`, 1–3 draws), keeper sample second (`sampleKeeperZone`, 1 draw).
 */
export function resolveShotLogic(
  vector: SwipeVector,
  dist: ZoneDistribution,
  prng: () => number,
  config: { moodDelta: number },
): ShotLogicResult {
  const swipe = aimToZone(vector, prng);            // draws 1..3
  const keeperZone = sampleKeeperZone(dist, prng);  // draws 1 — sample BEFORE shift
  const outcome = resolveShot(swipe.actualZone, keeperZone);
  const nextDist =
    swipe.actualZone !== 'off'
      ? applyMoodShift(dist, swipe.actualZone, config.moodDelta)
      : dist;                                        // off-frame = identity (PlayScene.ts:292)
  const shotResult: ShotResult = {
    targetZone: swipe.intendedZone === 'off' ? 'CA' : swipe.intendedZone,
    actualZone: swipe.actualZone,
    keeperZone,
    outcome,
  };
  return { swipe, keeperZone, outcome, nextDist, shotResult };
}

// Pure, prng-free. Lowest-p zone with p > 0, tie-break by ZONES order.
// Degenerate all-zero input (never produced in-game) falls back to ZONES[0].
export function lowestProbZone(dist: ZoneDistribution): Zone {
  let best: Zone = ZONES[0]!;
  let bestP = Infinity;
  for (const z of ZONES) {
    const p = dist[z];
    if (p > 0 && p < bestP) {
      bestP = p;
      best = z;
    }
  }
  return best;
}
